import { DifficultyLevel } from "../../Types/Game";
import "./DifficultyIndicator.css";

/**
 * DifficultyIndicator
 * --------------------
 * Renders the four-band difficulty gauge (see Assets/difficulty-indicator.svg,
 * the Inkscape source) as an inline SVG, colouring bands 1..level and greying
 * out the rest — a "fill meter" reading from level1 (bottom, easiest) up to
 * level4 (top, hardest). One parametric component instead of four static SVGs,
 * since browsers all support styling individual inline SVG elements from JS/CSS.
 */

// Witty, kid-friendly (10-15) rank names for each difficulty level.
export const DIFFICULTY_NAMES: Record<DifficultyLevel, string> = {
  1: "Junior Agent",
  2: "Field Agent",
  3: "Secret Agent",
  4: "Master Spy",
};

const BAND_COLORS: Record<DifficultyLevel, string> = {
  1: "#69c555", // green
  2: "#fed13e", // yellow
  3: "#fa9c38", // orange
  4: "#ed5330", // red
};

const INACTIVE_COLOR = "#8a8a8a";

type Props = {
  level: DifficultyLevel;
  className?: string;
};

const DifficultyIndicator = ({ level, className }: Props) => {
  const bandFill = (band: DifficultyLevel) =>
    band <= level ? BAND_COLORS[band] : INACTIVE_COLOR;

  return (
    <svg
      viewBox="0 0 46.09381 74.75"
      className={className}
      role="img"
      aria-label={`Difficulty: ${DIFFICULTY_NAMES[level]} (${level} of 4)`}
    >
      <g transform="translate(-1.9549764,-1.715361)">
        {/* level4 (top) */}
        <path
          fill={bandFill(4)}
          d="m 13.169837,4.8558895 h 23.672069 c 0.0267,0 6.03494,5.5470395 6.34112,6.7123255 l 3.08239,11.731211 c 0.30618,1.165285 -5.76488,3.176116 -9.07947,3.176116 H 13.169837 c -3.3145901,0 -9.7928301,-2.277603 -9.4235101,-3.424442 l 3.69887,-11.485951 C 7.8145169,10.41831 10.266237,7.3187365 13.169837,4.8528235 Z"
        />
        {/* level3 */}
        <path
          fill={bandFill(3)}
          transform="translate(-64.935722,-23.426178)"
          d="M 69.045578,50.551228 H 110.96611 V 64.319244 H 69.045578 Z"
        />
        {/* level2 */}
        <path
          fill={bandFill(2)}
          transform="translate(-65.141214,-10.274639)"
          d="M 69.045578,50.551228 H 110.96611 V 64.319244 H 69.045578 Z"
        />
        {/* level1 (bottom) */}
        <path
          fill={bandFill(1)}
          d="M 36.837916,74.352295 H 13.16585 c -0.02671,0 -6.0349391,-5.547039 -6.3411191,-6.712325 L 3.7423389,55.908759 c -0.30618,-1.165285 5.764873,-3.176114 9.0794651,-3.176114 h 24.016112 c 3.31459,0 9.79283,2.277601 9.42351,3.42444 l -3.69887,11.485951 c -0.36932,1.146839 -2.82104,4.246412 -5.72464,6.712325 z"
        />
        {/* shading overlay */}
        <path
          fill="rgba(0,0,0,0.35)"
          d="m 42.866446,9.5538105 -0.2055,28.5070435 c -0.11319,15.702103 -0.68113,30.353246 -9.350285,30.688017 -3.211757,0.124026 -11.493269,0.01245 -16.420767,0 C 8.9863449,68.728911 7.7065919,63.42 4.7698029,58.294087 l -0.410986,4.053101 C 3.6998549,68.845811 10.17013,74.335361 18.024455,74.335361 h 13.954853 c 7.854328,0 14.282118,-7.62 14.282118,-16.92 v -36.66 c 0,-5.02 -0.44122,-8.09155 -3.39498,-11.2015505 z"
        />
        {/* outline: band dividers + outer edge */}
        <g transform="matrix(0.83913727,0,0,1,-46.933161,-10.274639)" fill="#000000">
          <path
            transform="matrix(1.1917001,0,0,1,-21.698539,0)"
            d="M 109.66562,67.88 H 70.612175 c -1.317445,0 -2.38315,-1.26 -2.38315,-2.82 0,-1.56 1.065705,-2.82 2.38315,-2.82 h 39.053445 c 1.31745,0 2.38315,1.26 2.38315,2.82 0,1.56 -1.0657,2.82 -2.38315,2.82 z"
          />
          <path
            transform="matrix(1.1917001,0,0,1,-21.698539,0)"
            d="M 109.66562,53.77 H 70.612175 c -1.317445,0 -2.38315,-1.26 -2.38315,-2.82 0,-1.56 1.065705,-2.82 2.38315,-2.82 h 39.053445 c 1.31745,0 2.38315,1.26 2.38315,2.82 0,1.56 -1.0657,2.82 -2.38315,2.82 z"
          />
          <path
            transform="matrix(1.1917001,0,0,1,-21.698539,0)"
            d="M 109.66562,39.67 H 70.612175 c -1.317445,0 -2.38315,-1.26 -2.38315,-2.82 0,-1.56 1.065705,-2.82 2.38315,-2.82 h 39.053445 c 1.31745,0 2.38315,1.26 2.38315,2.82 0,1.56 -1.0657,2.82 -2.38315,2.82 z"
          />
          <path d="M 94.04,86.74 H 77.41 C 66.85,86.74 58.26,78.2 58.26,67.7 V 31.03 c 0,-10.5 8.59,-19.04 19.15,-19.04 h 16.63 c 10.56,0 19.15,8.54 19.15,19.04 V 67.7 c 0,10.5 -8.59,19.04 -19.15,19.04 z M 77.41,16.22 c -8.21,0 -14.89,6.64 -14.89,14.81 V 67.7 c 0,8.17 6.68,14.81 14.89,14.81 h 16.63 c 8.21,0 14.89,-6.64 14.89,-14.81 V 31.03 c 0,-8.17 -6.68,-14.81 -14.89,-14.81 z" />
        </g>
      </g>
    </svg>
  );
};

export default DifficultyIndicator;
